import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { translate } from '@/services/TranslationService';
import { NovovilleButton, Typography } from '@/components/novoville';
import ReCAPTCHAInput from '../auth/components/ReCAPTCHAInput';
import { withNOVOHeader } from '../sideMenu/HeaderHOC';

const CAPTCHA_VALUE_PARAM = 'param';

const PublicFormApplication = ({ history, setHeaderTitle }) => {
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [iframeHtml, setIframeHtml] = useState(null);
  const [isLoadingIframe, setIsLoadingIframe] = useState(false);
  const [iframeError, setIframeError] = useState(null);

  const deeplinkMeta = useSelector((state) => state.deeplinkReducer?.meta);
  const publicApplicationType = deeplinkMeta?.public_application_type || null;
  const publicApplicationTypeFound =
    deeplinkMeta?.public_application_type_found === true;
  const applicationTitle =
    publicApplicationType?.title || publicApplicationType?.name;

  useEffect(() => {
    setHeaderTitle?.(translate('sideMenu.Applications'));
  }, []);

  const goHome = () => {
    history.push('/');
  };

  const buildPublicFormUrl = () => {
    const CAPTCHA_KEY = process.env.REACT_APP_NV_CAPTCHA_KEY;
    const url = new URL(publicApplicationType.url);

    url.searchParams.set('nv-captcha-key', CAPTCHA_KEY || '');
    url.searchParams.set('nv-captcha-value-param', CAPTCHA_VALUE_PARAM);
    url.searchParams.set(CAPTCHA_VALUE_PARAM, recaptchaToken || '');

    if (deeplinkMeta?.application_licence_plate) {
      url.searchParams.set(
        'license_plate',
        deeplinkMeta.application_licence_plate
      );
    }

    if (deeplinkMeta?.applications_code) {
      url.searchParams.set('code', deeplinkMeta.applications_code);
    }

    return url;
  };

  const addBaseHref = (html, responseUrl) => {
    if (!html || !responseUrl || /<base\s/i.test(html)) return html;

    const baseHref = new URL(responseUrl).origin;
    const baseTag = `<base href="${baseHref}/">`;

    if (/<head[^>]*>/i.test(html)) {
      return html.replace(/<head([^>]*)>/i, `<head$1>${baseTag}`);
    }

    return `${baseTag}${html}`;
  };

  const continueToApplication = async () => {
    if (
      !recaptchaToken ||
      !publicApplicationType?.url ||
      iframeHtml ||
      isLoadingIframe
    ) {
      return;
    }

    const url = buildPublicFormUrl();
    const debugUrl = new URL(url.toString());
    debugUrl.searchParams.set('nv-captcha-key', '<redacted>');
    debugUrl.searchParams.set(CAPTCHA_VALUE_PARAM, '<redacted>');
    console.log('[PublicFormApplication] public form fetch URL', debugUrl.toString());

    setIframeError(null);
    setIsLoadingIframe(true);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'omit',
        redirect: 'follow',
        headers: {
          Accept:
            'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        },
      });
      const html = await response.text();

      console.log('[PublicFormApplication] public form fetch response', {
        status: response.status,
        ok: response.ok,
        url: response.url,
        contentType: response.headers.get('content-type'),
      });

      if (!html) {
        throw new Error(`Empty response body (${response.status})`);
      }

      setIframeHtml(addBaseHref(html, response.url || url.toString()));
    } catch (error) {
      console.log('[PublicFormApplication] public form fetch failed', error);
      setIframeError(
        'Δεν ήταν δυνατή η φόρτωση της φόρμας. Προσπαθήστε αργότερα.'
      );
    } finally {
      setIsLoadingIframe(false);
    }
  };

  if (!publicApplicationTypeFound) {
    return (
      <div className="relative flex flex-1 flex-col">
        <div className="m-auto flex w-full max-w-md flex-col items-center gap-6 rounded-md border border-gray-200 bg-white p-6 text-center shadow-sm">
          <div className="flex flex-col gap-2">
            <Typography variant="bold_24">Κάτι συνέβη</Typography>
            <Typography variant="regular_16">
              Δεν βρέθηκε η φόρμα ή υπάρχει κάποιο άλλο θέμα. Προσπαθήστε
              αργότερα.
            </Typography>
          </div>

          <NovovilleButton
            variant="primary"
            text={translate('Home')}
            onClick={goHome}
          />
        </div>
      </div>
    );
  }

  if (iframeHtml) {
    return (
      <div className="relative flex flex-1 flex-col">
        <iframe
          title={applicationTitle || publicApplicationType?.name}
          srcDoc={iframeHtml}
          allow="fullscreen *; clipboard-write *; camera *; microphone *; geolocation *; payment *"
          referrerPolicy="no-referrer-when-downgrade"
          className="h-full min-h-[calc(100vh-5rem)] w-full flex-1 border-0"
        />
      </div>
    );
  }

  if (isLoadingIframe || iframeError) {
    return (
      <div className="relative flex flex-1 flex-col">
        <div className="m-auto flex w-full max-w-md flex-col items-center gap-6 rounded-md border border-gray-200 bg-white p-6 text-center shadow-sm">
          <div className="flex flex-col gap-2">
            <Typography variant="bold_24">
              {iframeError ? 'Κάτι συνέβη' : 'Φόρτωση φόρμας'}
            </Typography>
            <Typography variant="regular_16">
              {iframeError || 'Παρακαλώ περιμένετε.'}
            </Typography>
          </div>

          {iframeError && (
            <NovovilleButton
              variant="primary"
              text={translate('Home')}
              onClick={goHome}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-1 flex-col">
      <div className="m-auto flex w-full max-w-md flex-col items-center gap-6 rounded-md border border-gray-200 bg-white p-6 text-center shadow-sm">
        <div className="flex flex-col gap-2">
          <Typography variant="bold_24">{applicationTitle}</Typography>
          <Typography variant="regular_16">
            Αν θέλετε να συνεχίσετε, ολοκληρώστε τον έλεγχο reCAPTCHA και
            πατήστε Συνέχεια.
          </Typography>
        </div>

        <ReCAPTCHAInput onVerify={setRecaptchaToken} />

        <div className="flex w-full flex-col gap-3 sm:flex-row">
          <NovovilleButton
            variant="primary"
            text="Συνέχεια"
            disabled={!recaptchaToken || isLoadingIframe}
            onClick={continueToApplication}
          />
          <NovovilleButton variant="outline" text="Ακύρωση" onClick={goHome} />
        </div>
      </div>
    </div>
  );
};

export default withNOVOHeader(withRouter(PublicFormApplication));
